import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { PasswordValidator } from '../utils/passwordValidation';

interface PasswordStrengthIndicatorProps {
  password: string;
  showDetails?: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showDetails = true
}) => {
  if (!password) {
    return null;
  }

  const validation = PasswordValidator.validatePassword(password);
  const strengthColor = PasswordValidator.getStrengthColor(validation.strength);
  const strengthText = PasswordValidator.getStrengthText(validation.strength);
  
  // Calculate progress percentage (0-100)
  const progressValue = Math.min((validation.score / 6) * 100, 100);

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      {/* Strength Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '0.75rem',
            minWidth: '60px',
            color: strengthColor,
            fontWeight: 500
          }}
        >
          {strengthText}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: strengthColor,
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* Validation Errors */}
      {showDetails && validation.errors.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {validation.errors.map((error, index) => (
            <Typography
              key={index}
              variant="caption"
              sx={{
                display: 'block',
                color: '#f44336',
                fontSize: '0.7rem',
                lineHeight: 1.2,
                mb: 0.25
              }}
            >
              • {error}
            </Typography>
          ))}
        </Box>
      )}

      {/* Success Message */}
      {validation.isValid && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: '#4caf50',
            fontSize: '0.7rem',
            mt: 0.5
          }}
        >
          ✓ Password meets all requirements
        </Typography>
      )}
    </Box>
  );
};

export default PasswordStrengthIndicator;